import {
  AuthProvider,
  FullPageSpinner,
  ThemeProvider,
  useAuth,
} from "@nexys/user-management-web";
import type { ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { um } from "./lib/client.js";
import { Shell } from "./pages/Shell.js";
import { SignInPage } from "./pages/SignIn.js";
import { SignUpPage } from "./pages/SignUp.js";
import { ForgotPasswordPage } from "./pages/ForgotPassword.js";
import { ResetPasswordPage } from "./pages/ResetPassword.js";
import { AcceptInvitationPage } from "./pages/AcceptInvitation.js";
import { AuthErrorPage, AuthVerifiedPage } from "./pages/AuthStatus.js";
import { DashboardPage } from "./pages/Dashboard.js";
import { UsersPage } from "./pages/Users.js";
import { UserDetailPage } from "./pages/UserDetail.js";
import { TenantsPage } from "./pages/Tenants.js";
import { TenantDetailPage } from "./pages/TenantDetail.js";
import { ProjectsPage } from "./pages/Projects.js";
import { ProjectDetailPage } from "./pages/ProjectDetail.js";
import { AuditLogsPage } from "./pages/AuditLogs.js";
import { EmailTestPage } from "./pages/EmailTest.js";
import { ProfilePage } from "./pages/Profile.js";

export const App = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthShellRoot />
      </BrowserRouter>
    </ThemeProvider>
  );
};

const AuthShellRoot = () => {
  const navigate = useNavigate();
  return (
    <AuthProvider
      client={um}
      onSignOut={() => navigate("/sign-in", { replace: true })}
      fallback={<FullPageSpinner />}
    >
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/verified" element={<AuthVerifiedPage />} />
        <Route path="/auth/error" element={<AuthErrorPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/accept-invitation/:id" element={<AcceptInvitationPage />} />
          <Route element={<Shell />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
            <Route path="/tenants" element={<TenantsPage />} />
            <Route path="/tenants/:id" element={<TenantDetailPage />} />
            <Route path="/products" element={<ProjectsPage />} />
            <Route path="/products/:id" element={<ProjectDetailPage />} />
            <Route path="/logs" element={<AuditLogsPage />} />
            <Route path="/email-test" element={<EmailTestPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<ProfilePage />} />
          </Route>
        </Route>
        <Route path="*" element={<Fallback />} />
      </Routes>
    </AuthProvider>
  );
};

const RequireAuth = ({ children }: { children?: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullPageSpinner />;
  if (status === "unauthenticated") {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
  return children ? <>{children}</> : <Outlet />;
};

const Fallback = () => {
  const { status } = useAuth();
  if (status === "loading") return <FullPageSpinner />;
  return <Navigate to={status === "authenticated" ? "/" : "/sign-in"} replace />;
};
